import {
  Item,
  ItemRestriction,
  OPERATORS,
  PROMOTION_TYPE,
  Restrictions,
  type RnbCalculatorPayload,
} from 'typings'
import { RestrictionsMap } from 'ui'
import { mapPromotionBase } from '..'
import {
  ApplyOnStatus,
  MaxShipping,
  NominalShipping,
  PercentualShipping,
  RegularBenefit,
  RegularBenefitVariant,
  RegularConditions,
  RegularFreeShipping,
  RegularScope,
  RegularScopeApplyTo,
  ShippingApplyTo,
  type RegularEffect,
  type RegularPromotion,
} from '../../../../typings'
import { EMPTY_SCOPE } from '../../../promotions/RegularForm/RegularEffect/Scope/constants'
import { benefitTypeByRestrictions } from '../../../promotions/RegularForm/RegularEffect/useRestrictionsByBenefit'
import {
  mapAffiliates,
  mapBin,
  mapClusterExpressions,
  mapFirstPurchase,
  mapHistoricalPurchaseValue,
  mapInstallments,
  mapItemPriceRange,
  mapListPrice,
  mapMarketingTags,
  mapOrderValue,
  mapPaymentMethods,
  mapShippingLocation,
  mapShippingMethods,
  mapSubscription,
  mapUTM,
  mapUTMICampaign,
} from '../conditions'
import { mapGift, mapGiftItems } from '../gift'
import {
  mapAccumulate,
  mapAccumulateWithManualPrices,
  mapBudget,
  mapMaxItemsPerAttribute,
  mapMaxItemsPerCart,
  mapMaxUsagePerClient,
  mapSalesChannels,
  mapSellerIds,
  mapUsage,
} from '../restrictions'

function mapConditions(data: RnbCalculatorPayload): RegularConditions {
  return {
    paymentMethods: mapPaymentMethods(data),
    orderValue: mapOrderValue(data),
    utm: mapUTM(data),
    cluster: mapClusterExpressions(data),
    bin: mapBin(data),
    shippingMethods: mapShippingMethods(data),
    shippingLocation: mapShippingLocation(data),
    listPrice: mapListPrice(data),
    installments: mapInstallments(data),
    itemPriceRange: mapItemPriceRange(data),
    affiliates: mapAffiliates(data),
    historicalPurchaseValue: mapHistoricalPurchaseValue(data),
    marketingTags: mapMarketingTags(data),
    firstPurchase: mapFirstPurchase(data),
    subscription: mapSubscription(data),
    utmiCampaign: mapUTMICampaign(data),
  }
}

type MapShippingsType = {
  type:
    | RegularBenefitVariant.MaxShipping
    | RegularBenefitVariant.NominalShipping
    | RegularBenefitVariant.PercentualShipping
    | RegularBenefitVariant.FreeShipping
  value: number
  payload: RnbCalculatorPayload
}

export function mapShippings(props: MapShippingsType) {
  const { payload, value, type } = props
  const { applyToAllShippings, slasIds } = payload

  const methods = slasIds?.length
    ? slasIds.map((id) => ({ id, name: id }))
    : undefined

  const mapApplyTo = () => {
    if (methods) {
      return ShippingApplyTo.selected
    }
    if (applyToAllShippings) {
      return ShippingApplyTo.all
    }
    return ShippingApplyTo.cheapest
  }

  return {
    type,
    applyTo: mapApplyTo(),
    ...(type !== RegularBenefitVariant.FreeShipping ? { value } : {}),
    ...(methods ? { selectedMethods: methods } : {}),
  } as NominalShipping | MaxShipping | RegularFreeShipping | PercentualShipping
}

type MapLoyaltyProps = {
  type:
    | RegularBenefitVariant.NominalLoyalty
    | RegularBenefitVariant.PercentualLoyalty
  value: number
  payload: RnbCalculatorPayload
}

export function mapLoyalty(props: MapLoyaltyProps): RegularBenefit {
  const { payload, value, type } = props
  const mapStatus: Record<
    RnbCalculatorPayload['orderStatusRewardValue'],
    ApplyOnStatus
  > = {
    invoiced: ApplyOnStatus.Invoiced,
    'payment-approved': ApplyOnStatus.PaymentApproved,
  }

  return {
    type,
    value,
    status: mapStatus[payload.orderStatusRewardValue],
  }
}

export function mapBenefit(data: RnbCalculatorPayload): RegularBenefit {
  const {
    nominalDiscountValue,
    percentualDiscountValue,
    nominalShippingDiscountValue,
    percentualShippingDiscountValue,
    absoluteShippingDiscountValue,
    maximumUnitPriceDiscount,
    nominalRewardValue,
    skusGift,
    percentualRewardValue,
    discountExpression,
    priceTableName,
  } = data

  if (nominalDiscountValue) {
    return {
      type: RegularBenefitVariant.Nominal,
      value: nominalDiscountValue,
    }
  }

  if (percentualDiscountValue) {
    return {
      type: RegularBenefitVariant.Percentual,
      value: percentualDiscountValue,
    }
  }

  if (nominalShippingDiscountValue) {
    return mapShippings({
      payload: data,
      value: nominalShippingDiscountValue,
      type: RegularBenefitVariant.NominalShipping,
    })
  }

  if (percentualShippingDiscountValue) {
    if (percentualShippingDiscountValue === 100) {
      return mapShippings({
        payload: data,
        value: data.percentualShippingDiscountValue,
        type: RegularBenefitVariant.FreeShipping,
      })
    }
    return mapShippings({
      payload: data,
      value: data.percentualShippingDiscountValue,
      type: RegularBenefitVariant.PercentualShipping,
    })
  }

  if (absoluteShippingDiscountValue) {
    return mapShippings({
      payload: data,
      value: data.absoluteShippingDiscountValue,
      type: RegularBenefitVariant.MaxShipping,
    })
  }

  if (maximumUnitPriceDiscount) {
    return {
      type: RegularBenefitVariant.MaxPrice,
      value: maximumUnitPriceDiscount,
    }
  }

  if (nominalRewardValue) {
    return mapLoyalty({
      type: RegularBenefitVariant.NominalLoyalty,
      value: nominalRewardValue,
      payload: data,
    })
  }

  if (percentualRewardValue) {
    return mapLoyalty({
      type: RegularBenefitVariant.PercentualLoyalty,
      value: percentualRewardValue,
      payload: data,
    })
  }

  if (discountExpression) {
    return {
      type: RegularBenefitVariant.Formula,
      expression: discountExpression,
    }
  }

  if (mapGiftItems(skusGift).length) {
    return mapGift(data, RegularBenefitVariant.Gift)
  }

  if (!!priceTableName?.length)
    return {
      type: RegularBenefitVariant.PriceTable,
      value: priceTableName,
    }

  throw new Error(
    'Cannot convert Promotion from API Payload: Invalid discount type variant',
    { cause: { payload: data } }
  )
}

const DEFAULT_OPERATOR = OPERATORS.EQUALS

function mapItems(
  items: Item[],
  isInclusive: boolean
): ItemRestriction | undefined {
  if (items.length === 0) return undefined

  return {
    items,
    operator: items.length
      ? isInclusive
        ? OPERATORS.EQUALS
        : OPERATORS.DIFFERENT
      : DEFAULT_OPERATOR,
  }
}

export function mapScope(data: RnbCalculatorPayload): RegularScope {
  const {
    skus,
    skusAreInclusive,
    collections,
    collectionsIsInclusive,
    products,
    productsAreInclusive,
    brands,
    brandsAreInclusive,
    categories,
    categoriesAreInclusive,
  } = data

  const allItems =
    skus.length +
      collections.length +
      products.length +
      brands.length +
      categories.length ===
    0

  if (allItems) {
    return {
      ...EMPTY_SCOPE,
      applyTo: RegularScopeApplyTo.allItems,
    }
  }

  return {
    applyTo: RegularScopeApplyTo.filteredItems,
    skus: mapItems(skus, skusAreInclusive),
    collections: mapItems(collections, collectionsIsInclusive),
    products: mapItems(products, productsAreInclusive),
    brands: mapItems(brands, brandsAreInclusive),
    categories: mapItems(categories, categoriesAreInclusive),
  }
}

export function mapRegularEffect(
  data: RnbCalculatorPayload
): NonNullableObject<RegularEffect> {
  return {
    scope: mapScope(data),
    benefit: mapBenefit(data),
  }
}

function restrictionIsEnabled(
  restriction: RestrictionsMap,
  benefitType: RegularBenefitVariant
): boolean {
  return benefitTypeByRestrictions[restriction]!!.includes(benefitType)
}

export function mapRegularRestrictions(
  data: RnbCalculatorPayload,
  benefit: RegularBenefit
): Restrictions {
  const { type } = benefit

  const baseRestrictions = {
    maxUsage: mapUsage(data),
    maxUsagePerClient: mapMaxUsagePerClient(data),
    accumulate: mapAccumulate(data),
    accumulateWithManualPrices: mapAccumulateWithManualPrices(data),
    sellers: mapSellerIds(data),
    budget: mapBudget(data),
    salesChannels: mapSalesChannels(data),
    origin: data.origin,
  }

  if (restrictionIsEnabled(RestrictionsMap.MAX_ITEMS_PER_CART, type)) {
    return {
      ...baseRestrictions,
      maxItemsPerCart: mapMaxItemsPerCart(data),
      maxItemsPerAttribute: undefined,
    }
  }

  if (restrictionIsEnabled(RestrictionsMap.MAX_ITEMS_PER_ATTRIBUTE, type)) {
    return {
      ...baseRestrictions,
      maxItemsPerAttribute: mapMaxItemsPerAttribute(data),
      maxItemsPerCart: undefined,
    }
  }

  return {
    ...baseRestrictions,
    maxItemsPerAttribute: undefined,
    maxItemsPerCart: undefined,
  }
}

export function mapRegular(data: RnbCalculatorPayload): RegularPromotion {
  const effect = mapRegularEffect(data)

  return {
    ...mapPromotionBase(data),
    type: PROMOTION_TYPE.REGULAR,
    conditions: mapConditions(data),
    regular: mapRegularEffect(data),
    restrictions: mapRegularRestrictions(data, effect.benefit),
  }
}
